// ---------------------------------------------------------------------------
// PWA_ControlBillet — Heartbeat Service
// Periodic ping to the Agent Caisse backend. Sets app online/offline status.
// Follows BorneInteractive pattern.
// ---------------------------------------------------------------------------

import { agentClient } from '@/services/agent-client'
import { useAppStore } from '@/store/app-store'
import { HEARTBEAT_INTERVAL } from '@/lib/constants'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FAILURE_THRESHOLD = 3

// ---------------------------------------------------------------------------
// Heartbeat Service
// ---------------------------------------------------------------------------

class HeartbeatService {
  private interval: ReturnType<typeof setInterval> | null = null
  private consecutiveFailures = 0

  // ---- lifecycle ----------------------------------------------------------

  start(): void {
    this.stop()
    this.consecutiveFailures = 0

    // Run immediately, then on interval
    this.check().catch(() => {})
    this.interval = setInterval(() => {
      this.check().catch(() => {})
    }, HEARTBEAT_INTERVAL)
  }

  stop(): void {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  // ---- check --------------------------------------------------------------

  private async check(): Promise<void> {
    const app = useAppStore.getState()

    const ok = await agentClient.heartbeat()

    if (ok) {
      this.consecutiveFailures = 0
      if (app.connectionStatus !== 'online') {
        useAppStore.getState().setOnline()
      }
    } else {
      this.consecutiveFailures += 1

      if (this.consecutiveFailures >= FAILURE_THRESHOLD) {
        useAppStore.getState().setOffline()
      } else {
        useAppStore.getState().setOffline()
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const heartbeatService = new HeartbeatService()
